import React, { useEffect, useState } from 'react'
import { maintenanceService } from '@/services/maintenanceService'
import { maintenanceStore } from '@/services/maintenanceStore'
import MaintenanceScreen from './MaintenanceScreen'
import '@/styles/components/maintenance.css'

/** How long to wait for the status check before assuming the portal is up. */
const CHECK_TIMEOUT_MS = 4000

type Status = 'checking' | 'down' | 'up'

/**
 * Gates the whole app on whether the portal is offline for maintenance.
 *
 * On load it asks the backend once. While it's waiting it shows a neutral
 * splash rather than the app, so a customer never sees the login form flash up
 * a moment before the maintenance notice replaces it. If the check is slow or
 * fails it opens the app anyway (fail open): a status glitch must never be able
 * to lock everyone out.
 *
 * It also listens for the portal being taken down mid-session (the API client
 * reports the 503 to maintenanceStore), so an already-open tab flips to the
 * notice without a reload.
 */
function MaintenanceGate({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<Status>('checking')
  const [message, setMessage] = useState('')

  useEffect(() => {
    let settled = false

    const goUp = (): void => {
      if (!settled) {
        settled = true
        setStatus('up')
      }
    }

    // Fail open if the check is slow, so a hung status endpoint can't leave the
    // whole portal stuck on the splash.
    const timer = window.setTimeout(goUp, CHECK_TIMEOUT_MS)

    maintenanceService
      .getStatus()
      .then(result => {
        if (settled) return
        settled = true
        if (result.maintenance) {
          setMessage(result.message)
          setStatus('down')
        } else {
          setStatus('up')
        }
      })
      .catch(goUp)

    return () => {
      settled = true
      window.clearTimeout(timer)
    }
  }, [])

  useEffect(() => {
    return maintenanceStore.subscribe(state => {
      if (state.down) {
        setMessage(state.message)
        setStatus('down')
      }
    })
  }, [])

  if (status === 'down') {
    return <MaintenanceScreen message={message} />
  }

  if (status === 'checking') {
    return (
      <div className="maintenance-screen" role="status" aria-label="Loading">
        <div className="maintenance-splash-spinner" aria-hidden="true" />
      </div>
    )
  }

  return <>{children}</>
}

export default MaintenanceGate
